import * as RadixNavigationMenu from '@radix-ui/react-navigation-menu';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Typography } from '@/components';
import { ArrowDownSLineIcon } from '@/components/icons/react/arrow-down-s-line';
import { getRel, getTarget } from '@/theme/shared/utils';

import { MegaMenu } from './MegaMenu';
import styles from './NavigationMenu.module.scss';
import { hasChildren } from '../../../utils';

import type { HeaderMenuItem } from '../../../types';

type NavigationMenuProps = {
  items: HeaderMenuItem[];
};

export function NavigationMenu({ items }: NavigationMenuProps) {
  // Controlled so force-mounted panels can carry an explicit open/closed state:
  // Radix omits data-state on NavigationMenu.Content when forceMount is used.
  const [activeValue, setActiveValue] = useState('');
  // The panel playing its exit animation; it stays visible until that ends. Only a
  // full close qualifies — switching hides the old panel at once, so the outgoing and
  // incoming links are never half-faded over each other.
  const [closingValue, setClosingValue] = useState('');
  const handleClosed = useCallback(() => setClosingValue(''), []);

  if (!items.length) {
    return null;
  }

  const changeActive = (next: string) => {
    setClosingValue(activeValue && !next ? activeValue : '');
    setActiveValue(next);
  };

  return (
    <RadixNavigationMenu.Root
      delayDuration={120}
      aria-label="Navigation Menu"
      value={activeValue}
      onValueChange={changeActive}
      onKeyDown={(event) => {
        // Radix has no trigger to restore focus to once the menu is controlled,
        // so Escape returns it to the trigger that opened the panel.
        if (event.key !== 'Escape' || !activeValue) {
          return;
        }

        const trigger = event.currentTarget.querySelector<HTMLElement>(
          `[data-interact-trigger="${activeValue}"]`,
        );

        changeActive('');
        trigger?.focus();
      }}
    >
      <RadixNavigationMenu.List className={styles.menu}>
        {items.map((item) => (
          <NavigationItem
            key={item.id}
            item={item}
            activeValue={activeValue}
            closing={closingValue === String(item.id)}
            onClosed={handleClosed}
          />
        ))}
        <RadixNavigationMenu.Indicator className={styles.menuIndicator}>
          <span className={styles.menuIndicatorBar} />
        </RadixNavigationMenu.Indicator>
      </RadixNavigationMenu.List>
      <div className={`${styles.viewportPosition} interact-header__mega`}>
        <RadixNavigationMenu.Viewport className={styles.viewport} forceMount />
      </div>
    </RadixNavigationMenu.Root>
  );
}

interface NavigationItemProps {
  item: HeaderMenuItem;
  activeValue: string;
  closing: boolean;
  onClosed: () => void;
}

function trackMenuInteraction(menuItem: string, menuLevel: 'top' | 'sub', parentItem?: string) {
  window.InteractTracking?.track('menu_interaction', {
    menu_item_name: menuItem,
    menu_level: menuLevel,
    parent_item: parentItem,
  });
}

function NavigationItem({ item, activeValue, closing, onClosed }: NavigationItemProps) {
  const value = String(item.id);
  const isOpen = activeValue === value;
  const contentRef = useRef<HTMLDivElement>(null);

  // Waiting on the running animations covers both completion and cancellation, which a
  // bare animationend handler misses when a panel is switched mid-transition. Clearing
  // happens in the async callback, never synchronously inside the effect body.
  useEffect(() => {
    if (!closing) {
      return;
    }

    let cancelled = false;
    const node = contentRef.current;
    const animations = node && typeof node.getAnimations === 'function' ? node.getAnimations() : [];

    // A paused or never-settling animation would otherwise strand the panel on screen.
    const settled = animations.length
      ? Promise.race([
          Promise.allSettled(animations.map((animation) => animation.finished)),
          new Promise((resolve) => setTimeout(resolve, 600)),
        ])
      : Promise.resolve();

    settled.then(() => {
      if (!cancelled) {
        onClosed();
      }
    });

    return () => {
      cancelled = true;
    };
  }, [closing, onClosed]);

  if (!hasChildren(item)) {
    return (
      <RadixNavigationMenu.Item key={item.id}>
        <RadixNavigationMenu.Link
          className={styles.menuLink}
          href={item.url || '#'}
          target={getTarget(item.target)}
          rel={getRel(item.target)}
          onClick={() => trackMenuInteraction(item.title, 'top')}
        >
          <Typography variant="label" size="medium" color="inherit">
            {item.title}
          </Typography>
        </RadixNavigationMenu.Link>
      </RadixNavigationMenu.Item>
    );
  }

  return (
    <RadixNavigationMenu.Item key={item.id} value={value}>
      <RadixNavigationMenu.Trigger
        data-interact-trigger={value}
        className={styles.menuTrigger}
        onClick={() => trackMenuInteraction(item.title, 'top')}
      >
        <Typography variant="label" size="medium" color="inherit">
          {item.title}
        </Typography>
        <ArrowDownSLineIcon
          aria-hidden="true"
          focusable="false"
          className={`${styles.menuCaret} ix-icon-18`}
          // Intrinsic size so it can't paint oversized before the header CSS applies.
          width={18}
          height={18}
          color="inherit"
        />
      </RadixNavigationMenu.Trigger>

      <RadixNavigationMenu.Content
        className={styles.content}
        forceMount
        data-state={isOpen ? 'open' : 'closed'}
        data-closing={!isOpen && closing ? '' : undefined}
        // Keeps the outgoing panel out of focus and the accessibility tree while it is
        // still visible for its exit animation.
        inert={isOpen ? undefined : ''}
        ref={contentRef}
      >
        <MegaMenu item={item} />
      </RadixNavigationMenu.Content>
    </RadixNavigationMenu.Item>
  );
}
